import { formatDisplayDate } from '../../utils/dateFormat';
import { formatMoneyInr } from '../../utils/moneyFormat';
import { buildSubscriptionChargeRows } from '../../utils/subscriptionChargeDisplay';
import {
  buildSlotPatternRows,
  formatSpecialtyWithRegion,
  resolveDietLabel,
  resolvePlanLabel,
  resolveRegionLabel,
  weekdaysInSubscriptionRange,
} from '../../utils/subscriptionDisplay';
import {
  formatDayList,
  isBuilderSubscriptionPayload,
} from './builderSubscriptionMeta';

const formatPreviewDate = (value = '') => formatDisplayDate(value, value || '');

export const formatPreviewValue = (value) => {
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
};

const formatRs = (value) => formatMoneyInr(value);

const buildItemScheduleLines = (payload, slotLabelById, products) => (
  (payload.itemSchedules || []).map((row) => {
    const slotLabel = slotLabelById[row.slotId] || row.slotId || 'Slot';
    const product = products.find((item) => item.id === row.productId && item.slotId === row.slotId)
      || products.find((item) => item.id === row.productId);
    const productName = product?.name || row.productId || 'Product';
    const activeDays = weekdaysInSubscriptionRange(
      payload.startDate,
      payload.endDate,
      row.days || [],
      payload.days || [],
    );
    const daysLabel = formatDayList(activeDays, 'full') || '—';
    return `${slotLabel}: ${productName} (${daysLabel})`;
  })
);

export const buildSubscriptionEmailPreview = ({
  payload = {},
  subscriptionId = '',
  lookupCode = '',
  slotLabelById = {},
}) => {
  const customer = payload.customer || {};
  const products = Array.isArray(payload.products) ? payload.products : [];
  const totalProductTypes = products.length;
  const totalProductQuantity = products.reduce(
    (sum, product) => sum + (Number(product.quantity) || 0),
    0,
  );
  const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
  const builderPayload = isBuilderSubscriptionPayload(payload);
  const specialtyLabel = formatSpecialtyWithRegion(payload);
  const regionOnly = resolveRegionLabel(payload);
  const mealTypeLabel = resolveDietLabel(payload)
    || (payload.specialtyOrganic != null || payload.isOrganic != null
      ? ((payload.specialtyOrganic ?? payload.isOrganic) ? 'Organic' : 'Regular')
      : null);
  const planLabel = resolvePlanLabel(payload, payload.planLabel || '—');
  const slotPatternRows = buildSlotPatternRows({
    ...payload,
    slotLabels: (payload.mealSlots || []).map((id) => slotLabelById[id] || id),
  });
  const activeDaysLabel = formatDayList(
    weekdaysInSubscriptionRange(payload.startDate, payload.endDate, payload.days || []),
    'full',
  ) || '—';
  const itemScheduleLines = builderPayload
    ? buildItemScheduleLines(payload, slotLabelById, products)
    : [];

  const referenceRows = [
    ['Subscription ID', subscriptionId || 'Assigned on confirmation'],
    ['Tracking Code', lookupCode || 'Assigned on confirmation'],
  ];

  const customerRows = [
    ['Full Name', fullName],
    ['Phone', customer.phone],
    ['Email', customer.email],
    ['Location / Area', customer.locationArea],
    ['Address', customer.address],
    ['City', customer.city],
    ['Country', customer.country],
    ['PIN Code', customer.pincode],
  ];

  const subscriptionRows = builderPayload ? [
    ['Title', payload.title],
    ['Specialty', specialtyLabel],
    ...(regionOnly ? [['Region', regionOnly]] : []),
    ...(mealTypeLabel ? [['Meal type', mealTypeLabel]] : []),
    ['Plan', planLabel],
    ['Start Date', formatPreviewDate(payload.startDate)],
    ['End Date', formatPreviewDate(payload.endDate) || '—'],
    ['Delivery Days Count', payload.deliveryDayCount ?? 0],
    ['Total Slots', payload.deliverySlotCount ?? 0],
    ['Products selected', totalProductTypes],
    ['Total quantity', totalProductQuantity],
  ] : [
    ['Title', payload.title],
    ['Specialty', specialtyLabel],
    ...(regionOnly ? [['Region', regionOnly]] : []),
    ...(mealTypeLabel ? [['Meal type', mealTypeLabel]] : []),
    ['Plan', planLabel],
    ['Schedule', payload.scheduleLabel],
    ['Selected Days', activeDaysLabel],
    ['Start Date', formatPreviewDate(payload.startDate)],
    ['End Date', formatPreviewDate(payload.endDate) || '—'],
    ['Delivery Days Count', payload.deliveryDayCount ?? 0],
    ['Total Slots', payload.deliverySlotCount ?? 0],
    ['Products selected', totalProductTypes],
    ['Total quantity', totalProductQuantity],
  ];

  const chargeRows = buildSubscriptionChargeRows(payload, formatRs);

  const productLines = products.map((product) => {
    const includedSuffix = product.includedInPlan ? ' (included in plan)' : '';
    const line = `${product.name || 'Product'} × ${product.quantity ?? 0} - ${formatRs(product.price ?? 0)}${includedSuffix}`;
    const slotLabel = product.slotId ? slotLabelById[product.slotId] : '';
    const daysLabel = Array.isArray(product.days) && product.days.length
      ? formatDayList(product.days, 'short')
      : '';
    const meta = [slotLabel, daysLabel].filter(Boolean).join(' · ');

    return { line, meta };
  });

  return {
    referenceRows,
    customerRows,
    subscriptionRows,
    chargeRows,
    productLines,
    itemScheduleLines,
    slotPatternRows,
    builderPayload,
  };
};
